import styled from 'styled-components';

import { ThemeProp } from '~/theme/Themes';

export const FaviconStackWrapper = styled.div`
  display: flex;
  align-items: center;
`;

export const FaviconTileList = styled.div`
  display: flex;
  align-items: center;

  /* Overlap tiles in a stack — only applies when used as a stack */
  & > * {
    margin-right: -0.3125rem;
  }

  & > *:last-child {
    margin-right: 0;
  }
`;

export const FaviconTile = styled.div`
  width: 1rem;
  height: 1rem;
  border-radius: 0.25rem;
  border: 0.372px solid #d6d6da;
  background: ${(p: ThemeProp) => p.theme.colors.neutrals.white};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: fill;
    display: block;
  }

  svg {
    width: 100%;
    height: 100%;
    color: ${(p: ThemeProp) => p.theme.colors.neutrals.c400};
  }
`;

export const FaviconStackLabel = styled.span`
  font-size: ${(p: ThemeProp) => p.theme.fontSize.xs};
  font-weight: ${(p: ThemeProp) => p.theme.fontWeight.regular};
  color: ${(p: ThemeProp) => p.theme.colors.neutrals.black};
  margin-left: 0.375rem;
  white-space: nowrap;
`;

export const SourcesFooterWrapper = styled.div`
  position: relative;
  display: inline-block;
  margin-top: 0.5rem;
`;

export const SourcesFooterTrigger = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  border-radius: ${(p: ThemeProp) => p.theme.borderRadius};

  &:focus-visible {
    outline: 2px solid ${(p: ThemeProp) => p.theme.colors.aiOrange};
    outline-offset: 2px;
  }
`;

export const SourcesPopoverPanel = styled.div<{
  $isOpen: boolean;
  $top: number;
  $left: number;
  $placement: 'above' | 'below';
  $maxHeight: number;
}>`
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  flex-direction: column;
  gap: 0.375rem;
  position: fixed;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  transform: ${({ $placement }) =>
    $placement === 'above' ? 'translateY(calc(-100% - 0.375rem))' : 'translateY(0.375rem)'};
  min-width: 15rem;
  max-width: 20rem;
  background: ${(p: ThemeProp) => p.theme.colors.neutrals.white};
  border: 1px solid ${(p: ThemeProp) => p.theme.colors.neutrals.c050};
  border-radius: 0.3125rem;
  box-shadow: 0 0.625rem 0.625rem rgba(0, 0, 0, 0.05), 0 0 0.15625rem rgba(0, 0, 0, 0.03);
  padding: 0.75rem;
  z-index: 9999;
  max-height: ${({ $maxHeight }) => $maxHeight}px;
  overflow-y: auto;
  overscroll-behavior: contain;

  &::-webkit-scrollbar {
    width: 0.375rem;
  }

  &::-webkit-scrollbar-track {
    background: ${(p: ThemeProp) => p.theme.colors.neutrals.c100};
    border-radius: 0.25rem;
  }

  &::-webkit-scrollbar-thumb {
    background: ${(p: ThemeProp) => p.theme.colors.neutrals.c300};
    border-radius: 0.25rem;

    &:hover {
      background: ${(p: ThemeProp) => p.theme.colors.neutrals.c400};
    }
  }
`;

export const SourceRowLink = styled.a`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  text-decoration: none;
  color: inherit;
  min-width: 0;
  width: 100%;
`;

export const SourceRowTitleLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  width: 100%;
`;

export const SourceRowTitle = styled.span`
  font-size: 0.8125rem;
  font-weight: ${(p: ThemeProp) => p.theme.fontWeight.semibold};
  color: ${(p: ThemeProp) => p.theme.colors.neutrals.c500};
  line-height: 1.125rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const SourceRowSubtitle = styled.span`
  font-size: 0.8125rem;
  font-weight: ${(p: ThemeProp) => p.theme.fontWeight.regular};
  color: ${(p: ThemeProp) => p.theme.colors.neutrals.c400};
  line-height: 1.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`;

export const CitationCardPanel = styled.div<{ $maxHeight: number; $placement: 'above' | 'below' }>`
  position: fixed;
  z-index: 9999;
  width: 17.5rem;
  background: ${(p: ThemeProp) => p.theme.colors.neutrals.white};
  border: 1px solid ${(p: ThemeProp) => p.theme.colors.neutrals.c050};
  border-radius: 0.3125rem;
  box-shadow: 0 0.625rem 0.625rem rgba(0, 0, 0, 0.05), 0 0 0.15625rem rgba(0, 0, 0, 0.03);
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: ${({ $maxHeight }) => $maxHeight}px;
  overflow-y: auto;
  overscroll-behavior: contain;
  transform: ${({ $placement }) =>
    $placement === 'above' ? 'translateY(calc(-100% - 0.375rem))' : 'translateY(0.375rem)'};
`;

export const CitationCardNav = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

export const CitationNavButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.125rem 0.25rem;
  font-size: ${(p: ThemeProp) => p.theme.fontSize.s};
  color: ${(p: ThemeProp) => p.theme.colors.neutrals.c500};
  border-radius: 0.25rem;

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }

  &:not(:disabled):hover {
    background: ${(p: ThemeProp) => p.theme.colors.neutrals.c050};
  }
`;

export const CitationNavCounter = styled.span`
  margin-left: auto;
  font-size: ${(p: ThemeProp) => p.theme.fontSize.xs};
  color: ${(p: ThemeProp) => p.theme.colors.neutrals.c400};
`;

export const CitationCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export const CitationCardTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
`;

export const CitationCardTitle = styled.span`
  font-size: ${(p: ThemeProp) => p.theme.fontSize.s};
  font-weight: ${(p: ThemeProp) => p.theme.fontWeight.semibold};
  color: ${(p: ThemeProp) => p.theme.colors.neutrals.black};
  line-height: 1.25rem;
`;

export const CitationCardTitleLink = styled.a`
  font-size: ${(p: ThemeProp) => p.theme.fontSize.s};
  font-weight: ${(p: ThemeProp) => p.theme.fontWeight.semibold};
  color: ${(p: ThemeProp) => p.theme.colors.neutrals.black};
  line-height: 1.25rem;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export const CitationCardExcerpt = styled.p`
  font-size: ${(p: ThemeProp) => p.theme.fontSize.xs};
  color: ${(p: ThemeProp) => p.theme.colors.neutrals.c400};
  line-height: 1.25rem;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
